type ParsedStringPart<Rules extends ReadonlyArray<string>> = {
    type: "literal";
    value: string; // is the literal value, otherwise it's the length
} | {
    type: Rules[number];
    value: number;
};

interface StringParserRule<Rules extends ReadonlyArray<string>> {
    readonly value: string; // Must be a single character
    readonly type: Rules[number]; // What the type should be called
}

export class InvalidTemplateValueError extends Error {
    constructor(
        public invalidCharacter: string,
        public expectingCharacter?: string
    ) {
        super();
    }
}

export abstract class StringParser {
    public static parseTemplate<RuleTypes extends ReadonlyArray<string>>(
        template: string,
        rules: ReadonlyArray<StringParserRule<RuleTypes>>,
        throwOnInvalid = false
    ): Array<ParsedStringPart<RuleTypes>> {
        const parts: Array<ParsedStringPart<RuleTypes>> = [];
        let nextIsLiteral = false;
        let currentType: false | string = false;
        let currentCount = 0;

        for(let i = 0, l = template.length; i < l; i++) {
            const value = template[i];

            if(nextIsLiteral) {
                parts.push({
                    type: "literal",
                    value,
                });

                nextIsLiteral = false;
                continue;
            }

            let type: false | string = false;

            if(value === "\\") {
                nextIsLiteral = true;
            } else {
                for(let j = 0, n = rules.length; j < n; j++) {
                    if(rules[j].value === value) {
                        type = rules[j].type;
                        break;
                    }
                }
            }

            let afterPart: false | ParsedStringPart<RuleTypes> = false;

            if(type === false) {
                // A literal type
                if(nextIsLiteral || !throwOnInvalid) {
                    afterPart = {
                        type: "literal",
                        value,
                    };
                } else {
                    throw new InvalidTemplateValueError(value);
                }

                if(currentType === false) {
                    parts.push(afterPart);
                    continue;
                }
            }

            if(type !== currentType) {
                if(currentType !== false) {
                    parts.push({
                        type : currentType,
                        value: currentCount,
                    });
                }

                currentType = type;
                currentCount = 1;
            } else {
                currentCount++;
            }

            if(afterPart && !nextIsLiteral) {
                parts.push(afterPart);
            }
        }

        if(!nextIsLiteral && currentType !== false) {
            parts.push({
                type : currentType,
                value: currentCount,
            });
        }

        return parts;
    }

    public static fromParts<RuleTypes extends ReadonlyArray<string>>(
        toParse: string,
        templateParts: Array<ParsedStringPart<RuleTypes>>
    ): Array<{ type: RuleTypes[number]; value: string; }> {
        const parts = [];
        let currentPart: false | RuleTypes[number] = false;
        let currentValue = "";
        let position = 0;

        for(let i = 0, l = templateParts.length; i < l; i++) {
            const part = templateParts[i];
            let value;

            if(part.type === "literal") {
                value = toParse.substr(position, 1);

                if(value !== part.value) {
                    throw new InvalidTemplateValueError(value, part.value as string);
                }

                position++;
            } else {
                value = toParse.substr(position, part.value as number);
                position += part.value as number;
            }

            if(currentPart === false) {
                currentPart = part.type;
            } else if(currentPart !== part.type) {
                parts.push({
                    type : currentPart,
                    value: currentValue,
                });

                currentPart = part.type;
                currentValue = "";
            }

            currentValue += value;
        }

        if(currentValue !== "" && currentPart !== false) {
            parts.push({
                type : currentPart,
                value: currentValue,
            });
        }

        return parts;
    }
}
